import axios from "axios";
import * as HttpStatus from "http-status-codes";
import i18n from "../i18n";
import { parseISO } from "date-fns";
import { BehaviorSubject } from "rxjs";
import { License } from "../models/LicenseModel";
import { Channel } from "../models/ChannelModel";
import { QualityIndexLog, SnmpConfigurationModel, TrapReceiverModel, SmtpConfigModel, EmailReceiverModel, WatermarkingConfigurationModel, LogConfigurationModel, WatermarkingConfigurationInkModel } from "acr-common-models";
import { WatermarkLog } from "models/WatermarkLogModel";
import { loginService } from "./login";
import { LicenseStringModel } from "models/LicenseStringModel";
import { sortChannels } from "./utils";

export const ERROR_NB_MAX_EMAIL = 20;

interface ILogResponse {
  logs: Array<QualityIndexLog | WatermarkLog>;
  totalLogCount: number;
}
class API {
  private static instance: API;
  private lastPageBeforNetworkError = "";
  private NETWORK_ERROR_PAGE = "/networkError";
  public serverDateSubject = new BehaviorSubject<Date>(new Date());
  public connexionStatusSubject = new BehaviorSubject<boolean | null>(null);
  public licenseSubject = new BehaviorSubject<License>(new License());
  public licenseSubjectSnap = new BehaviorSubject<License>(new License());
  public licenseSubjectInk = new BehaviorSubject<License>(new License());
  public licenseSubjects = new Map<string, BehaviorSubject<License>>([["snap",this.licenseSubjectSnap],["ink",this.licenseSubjectInk]]);
  public licenseStringSubject = new BehaviorSubject<LicenseStringModel>(new LicenseStringModel());
  public licenseStringSubjectSnap = new BehaviorSubject<LicenseStringModel>(new LicenseStringModel());
  public licenseStringSubjectInk = new BehaviorSubject<LicenseStringModel>(new LicenseStringModel());
  public licenseStringSubjects = new Map<string,BehaviorSubject<LicenseStringModel>>([["snap",this.licenseStringSubjectSnap],["ink",this.licenseStringSubjectInk]]);
  public channelsSubject = new BehaviorSubject<Array<Channel>>(new Array<Channel>());
  public versionSubject = new BehaviorSubject<string>("...");
  public trapReceiversSubject = new BehaviorSubject<TrapReceiverModel[]>(new Array<TrapReceiverModel>());
  public communityNameSubject = new BehaviorSubject<string>("Loading...");
  public serverStatusSubject = new BehaviorSubject<any>("Loading...");
  public monitorModeSubject = new BehaviorSubject<any>("");
  public isLocalStorageLoadedSubject = new BehaviorSubject<any>("Loading...");
  public emailReceiversSubject = new BehaviorSubject<EmailReceiverModel[]>(new Array<EmailReceiverModel>());
  public smtpConfigSubject = new BehaviorSubject<SmtpConfigModel>(new SmtpConfigModel());
  public logConfigurationSubject = new BehaviorSubject<LogConfigurationModel>(new LogConfigurationModel());
  public watermarkingConfigurationSubjectSnap = new BehaviorSubject<WatermarkingConfigurationModel|WatermarkingConfigurationInkModel>(
    new WatermarkingConfigurationModel()
  );
  public watermarkingConfigurationSubjectInk = new BehaviorSubject<WatermarkingConfigurationModel|WatermarkingConfigurationInkModel>(
    new WatermarkingConfigurationModel()
  );
  public watermarkingConfigurationSubjects = new Map<string,BehaviorSubject<WatermarkingConfigurationModel|WatermarkingConfigurationInkModel>>([["snap",this.watermarkingConfigurationSubjectSnap],["ink",this.watermarkingConfigurationSubjectInk]]);

  private constructor() {}

  public init(): void {   
    axios.defaults.baseURL = this.getBaseURL();
    axios.defaults.headers.get["Access-Control-Allow-Origin"] = "*";
    axios.interceptors.request.use(
      (request) => {
        if (window.location.href.indexOf("/settings/update") < 0) {
          const userLanguage = localStorage.getItem("language");
          if (userLanguage) {
            i18n.changeLanguage(userLanguage);
          }
        }
        const token = localStorage.getItem("access_token");
        if (token) {
          if(request && request.headers)
            request.headers["Authorization"] = "Bearer " + token;
        }
        this.isLocalStorageLoadedSubject.next(true);
        return request;
      },
      (error) => {
        console.error("Failed to add login header!", error);
      }
    );

    axios.interceptors.response.use(
      (response) => {
        if (window.location.href.indexOf(this.NETWORK_ERROR_PAGE) > 0) {
          if (this.lastPageBeforNetworkError.length > 0 && this.lastPageBeforNetworkError.indexOf(this.NETWORK_ERROR_PAGE) < 0) {
            window.location.href = this.lastPageBeforNetworkError;
          } else {
            window.location.href = "/mosaic";
          }
        }
        this.lastPageBeforNetworkError = window.location.href;
        this.connexionStatusSubject.next(true);
        return response;
      },
      (error) => {
        console.error(error);
        if (
          error.response &&
          (error.response.status === HttpStatus.BAD_REQUEST ||
            error.response.status === HttpStatus.NOT_FOUND ||
            error.response.status === HttpStatus.CONFLICT)
        ) {
          return Promise.reject(error.response);
        }
        if (error.response && error.response.status === HttpStatus.UNAUTHORIZED) {
          loginService.logout();        
          if (window.location.pathname !== '/login')
            window.location.href = '/login';
          return Promise.reject(error.response);
        }
        if (window.location.href.indexOf(this.NETWORK_ERROR_PAGE) < 0) {
          this.lastPageBeforNetworkError = window.location.href;
          window.location.href = this.NETWORK_ERROR_PAGE;
        }
        this.connexionStatusSubject.next(false);

        // Do something with response error
        if (!error.response) {
          console.error(`Server does not seems to respond:`, error);
          return Promise.reject(error.response);
        }
        return Promise.reject(error.response);
      }
    );
  }

  public static getInstance(): API {
    return this.instance || (this.instance = new this());
  }

  /////////////////// Observables
  /*public getServerClockObservab(): Observable<Date | null> {
    return this.serverDateSubject.asObservable();
  }*/

  /////////////////// API
  public async getStatus(): Promise<number | void> {
    return await axios
      .get("/status")
      .then((response) => {
        let serverDate = new Date();
        if (response.data && response.data.date) {
          serverDate = parseISO(response.data.date);
          this.serverDateSubject.next(serverDate);
        }
        const dataChannels: Channel[] = sortChannels(response.data.channels);
        let channels = new Array<Channel>();
        dataChannels?.forEach((dataChannel) => {
          const channel = new Channel({ ...dataChannel });
          channels.push(channel);
        });
        this.channelsSubject.next(channels);
        return response.status;
      })
      .catch((e) => {
        console.log(e);
      });
  }

  public async getVersion(): Promise<string> {
    let res = "...";
    await axios.get("/version").then((versionReq) => {
      res = versionReq.data || "...";
      this.versionSubject.next(res);
    }).catch((e) => {
      console.log(e);
    });
    return res;
  }

  public async getLicenseStatusByTechno(technology:string): Promise<License> {
    let license = new License();
    license.technology = technology;
    await axios.get("/license/status/techno/"+technology).then((response) => {
      if (response && response.data) {        
        this.licenseSubjects.get(technology)?.next(response.data);
      }
    }).catch((e) => {
      console.log(e);
    });
    return license;
  }

  public async getLicenseStatus(): Promise<License> {
    let license = new License();
    await axios.get("/license/status/dominant").then((response) => {
      if (response && response.data) {
        const licData = response.data;
        license.status = licData.status;
        license.qualityIndexEnable = licData.qualityIndexEnable;
        if (licData.expirationDate) {
          license.expirationDate = licData.expirationDate;
        }
        this.licenseSubject.next(license);
      }
    }).catch((e) => {
      console.log(e);
    });
    return license;
  }

  public async getLicenseStringByTechno(technology: string): Promise<LicenseStringModel> {
    let licenseStringModel = new LicenseStringModel();
    await axios.get("/license/techno/"+technology).then((response) => {
      if (response && response.data) {
        licenseStringModel.id = response.data.id;
        licenseStringModel.content = response.data.content;
        licenseStringModel.technology = technology; 
        this.licenseStringSubjects.get(technology)?.next(licenseStringModel);
      }
    }).catch((e) => console.error("Failed to get license string", e));
    return licenseStringModel;
  }

  public async getSnmpConfiguration(): Promise<void> {
    await axios
      .get("/snmp")
      .then((response) => {
        const { trapReceivers, communityName } = response.data;
        this.trapReceiversSubject.next(trapReceivers);
        this.communityNameSubject.next(communityName);
      })
      .catch((e) => console.error("Failed to get snmp configuration", e));
  }

  public async updateSnmpConfiguration(values: any): Promise<SnmpConfigurationModel> {
    return await axios
      .patch(`/snmp/configuration`, values)
      .then((response) => response.data)
      .catch((e) => console.error("Failed to update snmp configuration: ", e));
  }

  public async addTrapReceiver(trapReceiver: TrapReceiverModel): Promise<boolean> {
    let isConflicting = false;

    await axios
      .post(`/snmp/trapReceiver`, trapReceiver)
      .then((response) => {
        const updatedTrapReceivers = response.data;
        this.trapReceiversSubject.next(updatedTrapReceivers);
      })
      .catch((e) => {
        console.error("Failed to add trap receiver: ", e);
        isConflicting = e.status === HttpStatus.CONFLICT;
      });

    return isConflicting;
  }

  public async updateTrapReceiver(id: number, trapReceiver: TrapReceiverModel): Promise<boolean> {
    let isConflicting = false;
    await axios
      .patch(`/snmp/trapReceiver/${id}`, trapReceiver)
      .then((response) => {
        const updatedTrapReceivers = response.data;
        if (updatedTrapReceivers) {
          this.trapReceiversSubject.next(updatedTrapReceivers);
        }
      })
      .catch((e) => {
        console.error("Failed to update trap receiver: ", e);
        isConflicting = e.status === HttpStatus.CONFLICT;
      });
    return isConflicting;
  }

  public async deleteTrapReceiver(id: number): Promise<void> {
    await axios
      .delete(`/snmp/trapReceiver/${id}`)
      .then(async (_) => {
        const response = await axios.get("/snmp");
        const updatedTrapReceivers = response.data.trapReceivers;
        this.trapReceiversSubject.next(updatedTrapReceivers);
      })
      .catch((e) => console.error("Failed to delete trap receiver: ", e));
  }

  public async getEmailConfiguration(): Promise<boolean> {
    let errorNbEmail = false;
    await axios
      .get("/email")
      .then((response) => {
        const { emailReceivers, smtpConfig } = response.data;
        if (emailReceivers.length >= ERROR_NB_MAX_EMAIL) {
          errorNbEmail = true;
        }
        this.emailReceiversSubject.next(emailReceivers);
        this.smtpConfigSubject.next(smtpConfig);
      })
      .catch((e) => console.error("Failed to get email SMTP configuration", e));
    
    return errorNbEmail;
  }
  public async updateSmtpConfiguration(smtpConfig: SmtpConfigModel): Promise<boolean> {
    await axios
      .patch(`/email/configuration`, smtpConfig )
      .then((response) => response.data)
      .catch((e) => {
        console.error("Failed to update email SMTP configuration: ", e);
      });
    return true;
  }

  public async addEmailReceiver(emailReceiver: EmailReceiverModel): Promise<boolean> {
    let isConflicting = false;
    // Do not add the new email receiver if the number of email is up to ERROR_NB_MAX_EMAIL
    if (await this.getEmailConfiguration())
      return false;

    await axios
      .post(`/email/emailReceiver`, emailReceiver)
      .then((response) => {
        const updatedEmailReceivers = response.data;
        this.emailReceiversSubject.next(updatedEmailReceivers);
      })
      .catch((e) => {
        console.error("Failed to add email receiver: ", e);
        isConflicting = e.status === HttpStatus.CONFLICT;
      });

    return isConflicting;
  }
  public async updateEmailReceiver(id: number, emailReceiver: EmailReceiverModel): Promise<boolean> {
    let isConflicting = false;
    await axios
      .patch(`/email/emailReceiver/${id}`, emailReceiver)
      .then((response) => {
        const updatedEmailReceivers = response.data;
        if (updatedEmailReceivers) {
          this.emailReceiversSubject.next(updatedEmailReceivers);
        }
      })
      .catch((e) => {
        console.error("Failed to update email receiver: ", e);
        isConflicting = e.status === HttpStatus.CONFLICT;
      });
    return isConflicting;
  }
  public async deleteEmailReceiver(id: number): Promise<void> {
    await axios
      .delete(`/email/emailReceiver/${id}`)
      .then(async (_) => {
        const response = await axios.get("/email");
        const updatedEmailReceivers = response.data.emailReceivers;
        this.emailReceiversSubject.next(updatedEmailReceivers);
      })
      .catch((e) => console.error("Failed to delete email receiver: ", e));
  }

  public async getWatermarkingConfigurationByTechno(technology:string): Promise<void> {
    await axios
      .get(`/watermarkingConfiguration/techno/${technology}`)
      .then((response) => {
        const watermarkingConfiguration = response.data;
        this.watermarkingConfigurationSubjects.get(technology)?.next(watermarkingConfiguration);
      })
      .catch((e) => console.error("Failed to get watermarking configuration", e));
  }

  public async updateWatermarkingConfiguration(watermarkingConfigurationModel: WatermarkingConfigurationModel | WatermarkingConfigurationInkModel): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      axios
        .patch(`/watermarkingConfiguration/${watermarkingConfigurationModel.id}`, watermarkingConfigurationModel)
        .then((response) => {
          this.watermarkingConfigurationSubjects.get(watermarkingConfigurationModel.technology)?.next(watermarkingConfigurationModel);
          resolve(true);
        })
        .catch((e) => {
          reject(false);
        });
      });
  }

  public async getServerStatus(): Promise<void> {
    await axios
      .get("/maintenance/status")
      .then((response) => {
        const serverStatus = response.data;
        this.serverStatusSubject.next(serverStatus);
      })
      .catch((e) => console.error("Failed to get server status", e));
  }

  public async getLogConfiguration(): Promise<void> {
    await axios
      .get("/maintenance/logconfig")
      .then((response) => {
        const logConfiguration = response.data;
        this.logConfigurationSubject.next(logConfiguration);
      })
      .catch((e) => console.error("Failed to get log configuration", e));
  }

  public async updateLogConfiguration(logConfigurationModel: LogConfigurationModel) {
    await axios
      .post("/maintenance/logconfig", logConfigurationModel)
      .then((response) => {
        this.logConfigurationSubject.next(logConfigurationModel);
      })
      .catch((e) => console.error("Failed to update log configuration", e));
  }

  public async restart(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      axios.get(`/maintenance/restart`)
        .then(() => {
          return true;
        })
        .catch((e) => {
          console.error("Failed to restart ACR monitor:", e);
          return false;
        });
      });
  }

  public async getLogs(apiPrefix: string, channelId: number, logPerPageCount: number, pageNumber: number, technology :string = ""): Promise<ILogResponse> {
    let technoString = technology ? "&technology="+technology : "";
    let searchURL = `${apiPrefix}?channelId=${channelId}&count=${logPerPageCount}&pageNumber=${pageNumber}${technoString}`;
    const rawResponse = await axios.get(searchURL).then((rawResponse) => {
      const response: ILogResponse = {
        logs: rawResponse?.data.logs || [],
        totalLogCount: rawResponse?.data.count || 0,
      };
      return response;
    });
    return rawResponse;
  }

  private getBaseURL(): string {
    const domain = process.env.REACT_APP_SERVER_DOMAIN;
    const port = Number(process.env.REACT_APP_SERVER_PORT);
    let baseUrl: String;
    if (domain && port) {
      baseUrl = `${domain}:${port}`;
    } else if (port) {
      baseUrl = `http://${window.location.hostname}:${port}`;
    } else {
      baseUrl = window.location.origin;
    }
    const url = baseUrl + "/api/1.1";

    return url;
  }

  public async getMonitorMode(): Promise<boolean> {
    return new Promise<boolean>((resolve,reject) => {
      axios.get(`/status/mode`)
        .then((rawResponse) => {
          this.monitorModeSubject.next(rawResponse.data.mode);
          resolve(true);
        })
        .catch((e => {
          console.error("Failed to get monitor mode", e);
          resolve(false);
        }))
      });
  }
    

  public async updateLicenseString(newLicenseString: any) :Promise<boolean>{
    return new Promise<boolean>((resolve, reject) => {    
     axios
      .put("/license/"+newLicenseString.id, newLicenseString)
      .then((response) => {
        this.licenseStringSubjects.get(newLicenseString.id)?.next(newLicenseString);
        resolve(true);
      })
      .catch((e) => {
        console.error("Failed to update license string", e);
        resolve(false);}
        );
      }
    )
  }

  public async deleteLicenseString(id: number) :Promise<boolean>{
    return new Promise<boolean>((resolve, reject) => {    
     axios
      .delete("/license/"+id)
      .then((response) => {
        resolve(true);
      })
      .catch((e) => {
        console.error("Failed to delete license string", e);
        resolve(false);}
        );
      }
    )
  }
}


export const acrAPI = API.getInstance();
